import { Link, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  LayoutDashboard, Package, ShoppingCart, Percent, MessageCircle, FileText, Pencil,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

const quickLinks = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/orders', label: 'Bestellungen', icon: ShoppingCart },
  { to: '/admin/products', label: 'Produkte', icon: Package },
  { to: '/admin/discount-codes', label: 'Rabattcodes', icon: Percent },
  { to: '/admin/chat', label: 'Support', icon: MessageCircle },
  { to: '/admin/blog', label: 'Blog', icon: FileText },
];

function useContextEditLink() {
  const location = useLocation();
  const params = useParams<{ slug?: string; orderId?: string }>();
  const [editLink, setEditLink] = useState<{ to: string; label: string } | null>(null);

  useEffect(() => {
    const path = location.pathname;

    // Product page: /produkt/:slug
    if (path.startsWith('/produkt/') && params.slug) {
      supabase
        .from('products')
        .select('id')
        .eq('slug', params.slug)
        .maybeSingle()
        .then(({ data }) => {
          if (data) {
            setEditLink({ to: `/admin/products/${data.id}`, label: 'Produkt bearbeiten' });
          } else {
            setEditLink(null);
          }
        });
      return;
    }

    // Blog post page: /:slug resolved as blog post – check via slug
    // We detect blog posts by checking if a blog_posts row exists for the current slug
    if (params.slug && !path.startsWith('/produkt/')) {
      supabase
        .from('blog_posts')
        .select('id')
        .eq('slug', params.slug)
        .maybeSingle()
        .then(({ data }) => {
          if (data) {
            setEditLink({ to: `/admin/blog/${data.id}`, label: 'Beitrag bearbeiten' });
          } else {
            setEditLink(null);
          }
        });
      return;
    }

    setEditLink(null);
  }, [location.pathname, params.slug]);

  return editLink;
}

export default function AdminBar() {
  const { isAdmin, loading } = useAuth();
  const editLink = useContextEditLink();

  if (loading || !isAdmin) return null;

  return (
    <div className="sticky top-0 z-50 flex h-9 items-center gap-1 border-b border-neutral-800 bg-black px-3 text-xs text-white">
      {/* Context edit button */}
      {editLink && (
        <Link
          to={editLink.to}
          className="mr-2 flex items-center gap-1 rounded bg-white/20 px-2 py-1 font-medium transition-colors hover:bg-white/30"
        >
          <Pencil className="h-3 w-3" />
          {editLink.label}
        </Link>
      )}

      {/* Separator */}
      {editLink && <span className="mx-1 h-4 w-px bg-white/30" />}

      {/* Quick links */}
      {quickLinks.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          className="flex items-center gap-1 rounded px-2 py-1 transition-colors hover:bg-white/20"
        >
          <item.icon className="h-3 w-3" />
          <span className="hidden sm:inline">{item.label}</span>
        </Link>
      ))}
    </div>
  );
}
